/**
 * devBridge — tiny shared bridge for Dev Mode write-back (Phase 2).
 *
 * DevModeConnect owns the connection; App.tsx's `updateFiles` owns every edit.
 * This singleton lets them coordinate without prop-drilling: when Dev Mode is
 * connected, App.tsx asks the bridge whether a changed file should be written
 * back to the local `tile dev` server.
 *
 * Echo suppression (browser end): we record a content hash for every file that
 * arrives FROM the server (snapshot/edit). When `updateFiles` applies that same
 * content and would write it back, the hash matches → we skip it (it came from
 * disk, it's not a browser-origin change). The server has the mirror guard. So a
 * change travels exactly once, in the direction it originated. See docs/DEV-MODE.md.
 */

/** Fast non-crypto hash (cyrb53). Only needs to be stable within this tab — the
 *  server compares its own hashes; we never compare across the wire. */
function cyrb53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

const lastFromServer = new Map<string, string>(); // path -> hash of content received from disk

export const devBridge = {
  /** `<base>/__tiledev/write` when connected, else null. */
  writeUrl: null as string | null,
  /** Absolute path of the watched repo on disk (from the `tile dev` hello) —
   *  used to build a `vscode://file/...` deeplink for "Open in my editor". */
  editorDir: null as string | null,

  connect(writeUrl: string, editorDir?: string | null) {
    this.writeUrl = writeUrl;
    if (editorDir !== undefined) this.editorDir = editorDir ?? null;
  },
  reset() {
    this.writeUrl = null;
    this.editorDir = null;
    lastFromServer.clear();
  },

  /** Open a file in the dev's editor via the `vscode://` deeplink. Needs the
   *  absolute path, so it only works once `editorDir` is known (dev connected).
   *  Returns false if we can't build the link. */
  openInEditor(path: string): boolean {
    if (!this.editorDir || typeof document === 'undefined') return false;
    const abs = this.editorDir.replace(/\/+$/, '') + '/' + String(path).replace(/^\/+/, '');
    const url = 'vscode://file' + abs; // e.g. vscode://file/Users/me/app/App.js
    try {
      // An anchor click fires the OS protocol handler without navigating the SPA.
      const a = document.createElement('a');
      a.href = url;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    } catch {
      return false;
    }
  },

  /** Record a file that arrived FROM the local server (so its apply isn't echoed back). */
  noteIncoming(path: string, contents: string) {
    lastFromServer.set(path, cyrb53(contents));
  },

  /** True if this is a genuine browser-origin edit that should hit disk (not an echo). */
  shouldWriteBack(path: string, contents: string): boolean {
    if (!this.writeUrl) return false;
    return lastFromServer.get(path) !== cyrb53(contents);
  },

  /** Fire-and-forget: POST a changed file to the local tile dev server. */
  write(path: string, contents: string, type: 'CODE' | 'ASSET') {
    if (!this.writeUrl) return;
    try {
      void fetch(this.writeUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ path, contents, type }),
      }).catch(() => {});
    } catch {
      /* ignore — write-back is best-effort */
    }
  },
};
